import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Box, 
  CircularProgress,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Logo from '../components/Logo';
import Footer from '../components/Footer';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // E-Mail aus der Navigation übernehmen, wenn verfügbar
  const [email, setEmail] = useState(location.state?.email || '');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Status
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [codeVerified, setCodeVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Aktiver Schritt (1: Code eingeben, 2: Passwort festlegen)
  const [activeStep, setActiveStep] = useState(0);

  // Passwort-Validierung
  const validatePassword = (password: string): boolean => {
    // Mindestens 8 Zeichen, mindestens ein Großbuchstabe, ein Kleinbuchstabe und eine Zahl
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  };

  // Validiere Passwort bei Änderung
  useEffect(() => {
    if (newPassword) {
      if (!validatePassword(newPassword)) {
        setPasswordError(
          "Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten."
        );
      } else {
        setPasswordError("");
      }
    }
  }, [newPassword]);

  // Code verifizieren
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({
        text: 'Bitte gib eine E-Mail-Adresse ein.',
        type: 'error'
      });
      return;
    }
    
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`/api/password-reset/verify-code`, { 
        email, 
        resetCode 
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.valid) {
        setCodeVerified(true);
        setActiveStep(1);
        setMessage({ 
          text: 'Code wurde erfolgreich verifiziert. Du kannst nun ein neues Passwort festlegen.', 
          type: 'success' 
        });
      }
      
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || 'Ungültiger Code. Bitte versuche es erneut.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Passwort zurücksetzen
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setMessage({ 
        text: 'Die Passwörter stimmen nicht überein.', 
        type: 'error' 
      });
      return;
    }

    if (!validatePassword(newPassword)) {
      setMessage({ 
        text: 'Das Passwort erfüllt nicht die Sicherheitsanforderungen.', 
        type: 'error' 
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`/api/password-reset/reset`, {
        email,
        resetCode,
        newPassword
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      setMessage({ 
        text: response.data.message || 'Passwort wurde erfolgreich zurückgesetzt.', 
        type: 'success' 
      });
      
      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Überprüft ob Passwörter übereinstimmen
  const passwordsMatch = confirmPassword ? newPassword === confirmPassword : true;

  // Erneut Code senden
  const handleResendCode = async () => {
    if (!email) {
      setMessage({
        text: 'Bitte gib eine E-Mail-Adresse ein.',
        type: 'error'
      });
      return;
    }
    
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`/api/password-reset/request`, { email }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      setMessage({ 
        text: response.data.message || 'Eine E-Mail mit einem neuen Code wurde gesendet.', 
        type: 'success' 
      });
      
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <Box sx={{ 
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        pt: 4, pb: 2
      }}>
        {/* Logo und Titel */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mb: 2 
        }}>
          <Logo height={80} />
          <Typography variant="h4" component="h1" textAlign="center" gutterBottom>
            Meldeportal Trampolin
          </Typography>
        </Box>
        
        <Container component="main" maxWidth="sm">
          <Paper elevation={6} sx={{ padding: 4, display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
            <Typography variant="h4" gutterBottom>
              Passwort zurücksetzen
            </Typography>
            
            <Box sx={{ width: '100%', mb: 4 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                <Step>
                  <StepLabel>Code verifizieren</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Neues Passwort festlegen</StepLabel>
                </Step>
              </Stepper>
            </Box>

            {message && (
              <Alert severity={message.type} sx={{ width: '100%', mb: 2 }}>
                {message.text}
              </Alert>
            )}

            {/* Schritt 1: Code verifizieren */}
            {activeStep === 0 && (
              <Box component="form" onSubmit={handleVerifyCode} noValidate sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="E-Mail-Adresse"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                />
                
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                  Bitte gib den Code ein, den wir an Deine E-Mail-Adresse gesendet haben
                </Typography>
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="resetCode"
                  label="Reset-Code"
                  name="resetCode"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  disabled={loading}
                  inputProps={{ 
                    maxLength: 6,
                    style: { letterSpacing: '0.5em', fontSize: '1.2em', textAlign: 'center' }
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || !email || resetCode.length < 6}
                >
                  {loading ? <CircularProgress size={24} /> : 'Code überprüfen'}
                </Button>
                
                <Box textAlign="center" mt={2}>
                  <Button 
                    onClick={handleResendCode}
                    disabled={loading}
                    color="secondary"
                  >
                    Neuen Code senden
                  </Button>
                </Box>
              </Box>
            )}

            {/* Schritt 2: Neues Passwort festlegen */}
            {activeStep === 1 && (
              <Box component="form" onSubmit={handleResetPassword} noValidate sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label="Neues Passwort"
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  error={passwordError.length > 0}
                  helperText={passwordError}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Passwort bestätigen"
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  error={!passwordsMatch}
                  helperText={!passwordsMatch ? 'Die Passwörter stimmen nicht überein.' : ''}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || passwordError.length > 0 || !newPassword || !confirmPassword || !passwordsMatch}
                >
                  {loading ? <CircularProgress size={24} /> : 'Passwort zurücksetzen'}
                </Button>
                
                <Box display="flex" justifyContent="flex-start" width="100%" mt={2}>
                  <Button 
                    onClick={() => setActiveStep(0)}
                    disabled={loading}
                  >
                    Zurück
                  </Button>
                </Box>
              </Box>
            )}

            <Box mt={3} textAlign="center">
              <Typography variant="body2">
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  Zurück zum Login
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      {/* Footer am Ende der Seite */}
      <Box sx={{ mt: 'auto' }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default ResetPassword;